import React, { createContext, useContext, useEffect, useState } from "react";
import { parseJwt, checkTokenValidity } from "../utils/auth-debug";
import { getMe } from "../api/auth.api";

interface UserData {
  id: number;
  email: string;
  role: string;
  fullname?: string;
  phone?: string;
  profilePicture?: string;
  entrepriseId?: number;
  freeTrialStartedAt?: string | null;
  hasBeenOnboarded?: boolean;
  stripeCustomerId?: string | null;
}

interface AuthContextType {
  isAuthenticated: boolean;
  token: string | null;
  user: UserData | null;
  login: (token: string) => void;
  loginWithUserData: (token: string) => Promise<UserData>;
  logout: () => void;
  tokenInfo: any;
  refreshUser: () => Promise<void>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  token: null,
  user: null,
  login: () => {},
  loginWithUserData: async () => ({} as UserData),
  logout: () => {},
  tokenInfo: null,
  refreshUser: async () => {},
  isLoading: false,
});

export const useAuth = () => useContext(AuthContext);

// Cache management to prevent multiple simultaneous calls
let userDataCache: UserData | null = null;
let userDataPromise: Promise<UserData> | null = null;
let lastFetchTime = 0;
const CACHE_DURATION = 30000; // 30 seconds

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [token, setToken] = useState<string | null>(
    localStorage.getItem("accessToken")
  );
  const [user, setUser] = useState<UserData | null>(userDataCache);
  const [tokenInfo, setTokenInfo] = useState<any>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true); // Start with loading true

  // Cached getMe function to prevent multiple simultaneous calls
  const getCachedUserData = async (): Promise<UserData> => {
    const now = Date.now();

    // Return cached data if still valid
    if (userDataCache && now - lastFetchTime < CACHE_DURATION) {
      return userDataCache;
    }

    // Return existing promise if one is already in flight
    if (userDataPromise) {
      return userDataPromise;
    }

    // Create new promise
    userDataPromise = getMe().then(userData => {
      userDataCache = userData;
      lastFetchTime = now;
      userDataPromise = null;
      return userData;
    }).catch(error => {
      userDataPromise = null;
      throw error;
    });

    return userDataPromise;
  };

  const refreshUser = async () => {
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const userData = await getCachedUserData();
      setUser(userData);
    } catch (error) {
      console.error("Failed to refresh user data:", error);
      // Clear invalid auth state
      setIsAuthenticated(false);
      setUser(null);
      userDataCache = null;
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize authentication state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem("accessToken");

      if (!storedToken) {
        setIsLoading(false);
        return;
      }

      try {
        const info = parseJwt(storedToken);
        const { isValid } = checkTokenValidity(storedToken);

        setTokenInfo(info);

        if (!isValid) {
          localStorage.removeItem("accessToken");
          setToken(null);
          setUser(null);
          setIsAuthenticated(false);
          userDataCache = null;
          setIsLoading(false);
          return;
        }

        setToken(storedToken);
        setIsAuthenticated(true);

        // Fetch user data
        const userData = await getCachedUserData();
        setUser(userData);

      } catch (error) {
        localStorage.removeItem("accessToken");
        setToken(null);
        setIsAuthenticated(false);
        setUser(null);
        userDataCache = null;
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []); // Run only once on mount

  useEffect(() => {
    if (token) {
      try {
        const info = parseJwt(token);
        const { isValid } = checkTokenValidity(token);

        setTokenInfo(info);
        setIsAuthenticated(isValid);

        if (!isValid) {
          localStorage.removeItem("accessToken");
          setToken(null);
          setUser(null);
          userDataCache = null;
        }
      } catch (e) {
        localStorage.removeItem("accessToken");
        setToken(null);
        setIsAuthenticated(false);
        setUser(null);
        userDataCache = null;
      }
    } else {
      setIsAuthenticated(false);
      setTokenInfo(null);
      setUser(null);
      userDataCache = null;
    }
  }, [token]);

  const login = (newToken: string) => {
    localStorage.setItem("accessToken", newToken);
    setToken(newToken);
    // Clear cache on new login
    userDataCache = null;
    lastFetchTime = 0;
  };

  const loginWithUserData = async (newToken: string): Promise<UserData> => {
    localStorage.setItem("accessToken", newToken);
    setToken(newToken);
    // Clear cache on new login
    userDataCache = null;
    lastFetchTime = 0;

    // Fetch user data immediately with the new token
    try {
      const userData = await getMe();
      userDataCache = userData;
      lastFetchTime = Date.now();
      setUser(userData);
      setIsAuthenticated(true);
      return userData;
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem("accessToken");
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    userDataCache = null;
    lastFetchTime = 0;
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        token,
        user,
        login,
        loginWithUserData,
        logout,
        tokenInfo,
        refreshUser,
        isLoading
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
